import config from '@/common/config'
import store from '@state/store'
import { isHttpStatus } from '@/helpers/http-status-helpers'
import axios from 'axios'

class BaseApiService {
  /**
   * Api version (e.g. 1.0)
   */
  apiVersion = config.get('apiVersion')

  /**
   * Axios client
   */
  client = axios.create({
    baseURL: config.get('apiUrl'),
    json: true,
  })

  /**
   * HTTP methods
   */
  method = {
    GET: 'get',
    POST: 'post',
    DELETE: 'delete',
    PATCH: 'patch',
    PUT: 'put',
  }

  /**
   * A particular resource, e.i. users, posts, comments etc.
   */
  resource

  constructor(resource) {
    if (!resource) throw new Error('Resource is not provided')
    this.resource = resource
  }

  /**
   * Gets the full url for the endpoint
   * @param {String} args has the remaining fragement of the url
   * @param {Object} query key pair list of query args that will be mapped if provided e.g. `{ first: 'value', second: 'value' }`
   * @returns {String} full url including base
   */
  getUrl(args = '', query = null) {
    return `v${this.apiVersion}/${this.resource}${args ? `/${args}` : ''}${
      query ? `?${this.mapQueryParams(query)}` : ''
    }`
  }

  async handleErrors(err) {
    // If unauthorised, renew access token then retry
    if (isHttpStatus(err?.response?.status, 'Unauthorized')) {
      await store.dispatch('auth/getAccessTokenOrRefresh', true)

      return {
        retry: true,
        err,
      }
    }

    throw err
  }

  /**
   * Compiles request configuration and handles tasks like generating headers
   * list and retrieving the auth token
   * @param {String} method HTTP Method (GET, POST, PATCH, DELETE, PUT)
   * @param {String} url Endpoint url
   * @param {*} data Payload to send to server
   * @param {*} headers Request headers to send to server
   * @param {Boolean} isBlob Sets the response type to 'blob'
   * @returns Request config object
   */
  async compileRequestConfig(
    method,
    url,
    data,
    providedHeaders,
    isBlob = false
  ) {
    const accessToken = await store.dispatch('auth/getAccessTokenOrRefresh')
    if (typeof accessToken === 'undefined' || !accessToken) {
      throw Error('An access token is required for authenticated requests')
    }

    let impersonateHeader = {}

    // Set impersonate header if impersonate id is present
    if (store.getters['auth/hasImpersonateContactId']) {
      impersonateHeader = {
        'Impersonated-Contact': store.getters['auth/impersonateContactId'],
      }
    }

    // Replace versioned URLs to use accept header to request API version
    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'Accept-Version': this.apiVersion,
      ...providedHeaders,
      ...impersonateHeader,
    }

    let config = {
      method,
      url,
      data,
      headers,
    }

    if (isBlob) config = { ...config, ...{ responseType: 'blob' } }

    return config
  }

  /**
   * Executes an authenticated HTTP request
   * @param {String} method HTTP Method (GET, POST, PATCH, DELETE, PUT)
   * @param {String} url Endpoint url
   * @param {*} data Payload to send to server
   * @param {*} headers Request headers to send to server
   * @param {Boolean} isBlob Sets the response type to 'blob'
   * @returns Http response
   */
  async execute(method, url, data, providedHeaders, isBlob = false) {
    let config = await this.compileRequestConfig(
      method,
      url,
      data,
      providedHeaders,
      isBlob
    )

    try {
      return await this.client(config)
    } catch (err) {
      const response = await this.handleErrors(err)

      if (response.retry) {
        // Recompile config to utilise new auth token
        config = await this.compileRequestConfig(
          method,
          url,
          data,
          providedHeaders,
          isBlob
        )

        return await this.client(config)
      }
    }
  }

  /**
   * Executes an authenticated HTTP request for blob files
   * @param {String} method HTTP Method (GET, POST, PATCH, DELETE, PUT)
   * @param {String} url Endpoint url
   * @param {*} data Payload to send to server
   * @param {*} headers Request headers to send to server
   * @returns Http response
   */
  async executeBlob(method, url, data, providedHeaders) {
    return this.execute(method, url, data, providedHeaders, true)
  }

  /**
   * Executes an unauthenticated HTTP request
   * @param {String} method HTTP Method (GET, POST, PATCH, DELETE, PUT)
   * @param {String} url Endpoint url
   * @param {*} data Payload to send to server
   * @returns Http response
   */
  async executeAnon(method, url, data) {
    return this.client({
      method,
      url,
      data,
      headers: {
        'Accept-Version': this.apiVersion,
      },
    })
  }

  /**
   * Mapper that accepts a 1D object and generates a query string to be appended to a URL
   * @param {Object} queryParams key value object { key: value, ... }
   * @returns A query string e.g. ?key=value&key2=value2...
   */
  mapQueryParams(queryParams) {
    return queryParams
      ? Object.keys(queryParams)
          .map(function(key) {
            return key + '=' + queryParams[key]
          })
          .join('&')
      : ''
  }
}

class ReadOnlyApiService extends BaseApiService {
  /**
   * Generic configurable authenticated HTTP request
   * @param {String} method HTTP Method (GET, POST, PATCH, DELETE, PUT)
   * @param {String} url Endpoint url
   * @param {*} data Payload to send to server
   * @param {*} headers Request headers to send to server
   * @returns Http response
   */
  async fetch(method, url, data, headers) {
    return this.execute(method, url, data, headers)
  }

  /**
   * HTTP Get authenticated request
   * @param {*} args
   * @param {*} query query object {key: value}
   * @returns
   */
  async get(args, query = null) {
    return this.execute(this.method.GET, this.getUrl(args, query))
  }

  /**
   * Generic configurable unauthenticated HTTP request
   * @param {String} method HTTP Method (GET, POST, PATCH, DELETE, PUT)
   * @param {String} url Endpoint url
   * @param {*} data Payload to send to server
   * @param {*} headers Request headers to send to server
   * @returns Http response
   */
  async fetchAnon(method, url, data, headers) {
    return this.executeAnon(method, url, data, headers)
  }

  /**
   * HTTP Get unauthenticated request
   * @param {*} args
   * @param {*} query query object {key: value}
   * @returns
   */
  async getAnon(args, query = null) {
    return this.executeAnon(this.method.GET, this.getUrl(args, query))
  }

  /**
   * Generic configurable Authenticated HTTP request - Response type: Blob
   * @param {String} method HTTP Method (GET, POST, PATCH, DELETE, PUT)
   * @param {String} url Endpoint url
   * @param {*} data Payload to send to server
   * @param {*} headers Request headers to send to server
   * @returns Http response
   */
  async fetchBlob(method, url, data, headers) {
    return this.executeBlob(method, url, data, headers)
  }

  /**
   * HTTP Get authenticated request - Response type: Blob
   * @param {*} args
   * @param {*} query query object {key: value}
   * @returns
   */
  async getBlob(args, query = null) {
    return this.executeBlob(this.method.GET, this.getUrl(args, query))
  }
}

class ModelApiService extends ReadOnlyApiService {
  /**
   * HTTP Post authenticated request
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async post(args, data = {}, query = null) {
    return this.execute(this.method.POST, this.getUrl(args, query), data)
  }

  /**
   * HTTP Post unauthenticated request
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async postAnon(args, data = {}, query = null) {
    return this.executeAnon(this.method.POST, this.getUrl(args, query), data)
  }

  /**
   * HTTP Post authenticated request - Response type: Blob
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async postBlob(args, data = {}, query = null) {
    return this.executeBlob(this.method.POST, this.getUrl(args, query), data)
  }

  /**
   * HTTP Put authenticated request
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async put(args, data = {}, query = null) {
    return this.execute(this.method.PUT, this.getUrl(args, query), data)
  }

  /**
   * HTTP Put unauthenticated request
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async putAnon(args, data = {}, query = null) {
    return this.executeAnon(this.method.PUT, this.getUrl(args, query), data)
  }

  /**
   * HTTP Put authenticated request - Response type: Blob
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async putBlob(args, data = {}, query = null) {
    return this.executeBlob(this.method.PUT, this.getUrl(args, query), data)
  }

  /**
   * HTTP Patch authenticated request
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async patch(args, data = {}, query = null) {
    return this.execute(this.method.PATCH, this.getUrl(args, query), data)
  }

  /**
   * HTTP Patch unauthenticated request
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async patchAnon(args, data = {}, query = null) {
    return this.executeAnon(this.method.PATCH, this.getUrl(args, query), data)
  }

  /**
   * HTTP Delete authenticated request
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async delete(args, data = {}, query = null) {
    return this.execute(this.method.DELETE, this.getUrl(args, query), data)
  }

  /**
   * HTTP Delete unauthenticated request
   * @param {String} args Url arguments
   * @param {*} data Payload
   * @param {*} query query object {key: value}
   * @returns
   */
  async deleteAnon(args, data = {}, query = null) {
    return this.executeAnon(this.method.DELETE, this.getUrl(args, query), data)
  }
}

export { ReadOnlyApiService, ModelApiService }
